import React, { useRef, useState, useEffect, forwardRef } from 'react';
import cn from 'classnames';
import headerBarStyles from '@app/common/PageHeader/HeaderBar/index.module.less';
import styles from './cartMenu.module.less';
import { Button, Spin, Checkbox } from 'antd';
import CartEmpty from '@base/cart/CartEmpty';
import { CartType } from '@base/cart/useCart';
import { MissPriceArr } from '@base/checkout/constant';
import { getWindowHeight } from 'ava-util/dist/dom-window';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import CartActivityTips from '@base/cart/CartActivityTips';
import CartGroups from '@app/common/PageHeader/HeaderCart/CartGroups';
import { useTrans } from 'base/utils/i18n';

declare interface IProps {
    visible: boolean;
    cart: CartType;
}

const CartMenu = forwardRef<HTMLDivElement, IProps>((props, ref) => {
    const listRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const { t } = useTrans();
    const {
        visible,
        cart
    } = props;

    const {
        items,
        checkedTotalQty,
        cartLoading,
        summaryData,
        summaryLoading,
        goCheckout,
        //checkbox
        checkAll,
        checkAllDisabled,
        indeterminate,
        onCheckAll,
    } = cart;

    const setListHeight = () => {
        const list = listRef?.current;
        if (!list) {
            return;
        }
        setTimeout(() => {
            const summaryHeight = (list.parentNode as HTMLDivElement).offsetHeight - list.offsetHeight;
            const listTop = list.getBoundingClientRect().top;
            const maxHeight = getWindowHeight() - listTop - summaryHeight - 160;
            list.style.maxHeight = `${maxHeight}px`;
            list.scrollTop = 0;
        }, 100);
    };

    const handleGoCheckout = async () => {
        setLoading(true);
        await goCheckout();
        setLoading(false);
    };

    useEffect(() => {
        if (visible) {
            setListHeight();
        }
    }, [items, visible]);

    useEffect(() => {
        addEvent(window, 'resize', setListHeight);
        return () => {
            removeEvent(window, 'resize', setListHeight);
        };
    }, []);

    return (
        <div
            ref={ref}
            className={cn(headerBarStyles.submenu, styles['cart-menu'])}
        >
            {cartLoading ? <Spin className={styles.spin}/> : (
                Object.keys(items).length > 0 ? (
                    <>
                        <div className={styles['scroll-container']}>
                            <CartActivityTips
                                type="simple"
                                className={styles['activity-tips']}
                                cart={cart}
                            />
                            <div className={styles.list} ref={listRef}>
                                <CartGroups cart={cart}/>
                            </div>
                        </div>

                        <div className={styles['menu-footer']}>
                            <div className={cn(styles['menu-all-summary'], 'flex flex-justify')}>
                                <Checkbox
                                    className={styles['checkbox-common']}
                                    indeterminate={indeterminate}
                                    onChange={onCheckAll}
                                    checked={checkAll}
                                    disabled={checkAllDisabled}
                                >
                                    {t('web_common_all')}
                                </Checkbox>

                                <Spin spinning={summaryLoading}>
                                    <div className={styles.summary}>
                                        {!MissPriceArr.includes(summaryData.retailDiscount) && (
                                            <div className={styles.retail}>
                                                <span className={styles.label}>{t('web_summary_retail_price')}:</span>
                                                <span>
                                                    {summaryData.retailPriceExchangeDisplay}
                                                </span>
                                            </div>
                                        )}

                                        <div className={styles.total}>
                                            <span className={styles.label}>{t('web_summary_total')}:</span>
                                            <span className={cn({ [styles.discount]: !MissPriceArr.includes(summaryData?.retailDiscount) })}>
                                                {summaryData.totalPriceExchangeDisplay}
                                            </span>
                                        </div>
                                        {!!summaryData.tradeInPoints && (
                                            <div className={styles['trade-in-poings']}>
                                                {t('web_common_plus_points', {num: summaryData.tradeInPoints})}
                                            </div>
                                        )}
                                    </div>
                                </Spin>
                            </div>

                            <Button
                                block
                                size="large"
                                type="primary"
                                className="upper"
                                disabled={!summaryLoading && checkedTotalQty === 0}
                                loading={loading || summaryLoading}
                                onClick={handleGoCheckout}
                            >
                                {t('web_common_btn_checkout_upper')} ({checkedTotalQty})
                            </Button>
                        </div>
                    </>
                ) : (
                    <CartEmpty className={styles.empty}/>
                )
            )}
        </div>
    )
});

export default React.memo(CartMenu);
